import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { CheckCircle, ExclamationTriangle, Trash } from 'react-bootstrap-icons';
import DropdownSelect from '../Generic/DropdownSelect';
import { getSupplierCredits } from '../../clients/supplier_credit_client';
import { logInfo, logWarn } from '../../config_and_helpers/logger';
import { findUserOption } from '../../config_and_helpers/userDropdownOptions';
import NumberInput from '../Generic/NumberInput';
import {
  formatNumericInput,
  isValidNumericInput,
  parseNumericInput,
} from '../../utils/numberInput';
import { buildQuoteFundingFields } from '../../utils/quoteFundingPayload';
import BudgetLineSelect from '../LabData/BudgetLineSelect';

const LOG_CONTEXT = 'QuoteOcrReviewStep';

function toNumberOrNaN(value) {
  const n = parseNumericInput(value);
  return n === null ? NaN : n;
}

function formatMoneyLike(value) {
  const n = toNumberOrNaN(value);
  if (Number.isNaN(n)) return String(value ?? '');
  return n.toFixed(2);
}

function normalizeQuantityInput(value) {
  const raw = String(value ?? '').trim();
  if (raw === '') return '';
  const parsed = parseNumericInput(raw, { integerOnly: true });
  if (parsed === null) return raw;
  return formatNumericInput(parsed, { integerOnly: true }) || String(parsed);
}

/**
 * Unit price after a percent discount. The typed discount is always a percent:
 * 10 on a price of 200 is 180. A blank discount leaves the unit price.
 *
 * @param {string|number} price
 * @param {string|number} discount
 * @returns {string}
 */
export function computePriceAfterDiscount(price, discount) {
  const priceNum = toNumberOrNaN(price);
  if (Number.isNaN(priceNum)) return String(price ?? '');
  const raw = String(discount ?? '').trim();
  if (raw === '') return formatMoneyLike(priceNum);
  const discountNum = toNumberOrNaN(discount);
  if (Number.isNaN(discountNum)) return formatMoneyLike(priceNum);
  const out = priceNum * (1 - discountNum / 100);
  return formatMoneyLike(Math.max(0, out));
}

/**
 * Review step for OCR-parsed supplier quote payload before creating a quote.
 * Branches on `reviewData.mode`: `prepayment` (header-only, empty items) vs `products` (line items table).
 *
 * @param {Object} props
 * @param {Object} props.reviewData - Normalized parse payload from `normalizeSupplierQuoteOcrResponse`.
 * @param {'products'|'prepayment'} props.reviewData.mode - Parser mode from backend (authoritative).
 * @param {string} props.token - Auth token for supplier credit fetch.
 * @param {Array} props.supplierOptions - Supplier select options ({ value, label }).
 * @param {Array} props.projectOptions - Project select options.
 * @param {Array} props.userOptions - User select options.
 * @param {boolean} props.isLoadingProjects
 * @param {boolean} props.isLoadingUsers
 * @param {number|null} props.selectedProject
 * @param {number|null} props.selectedUser
 * @param {Function} props.onProjectChange - (value:number|null) => void
 * @param {Function} props.onUserChange - (value:number|null) => void
 * @param {Function} props.onCancel
 * @param {Function} props.onConfirm - receives final create payload for POST /quotes/
 * @param {boolean} props.isSubmitting
 * @param {string} props.submitError
 * @param {boolean} props.disabled
 */
const QuoteOcrReviewStep = ({
  reviewData,
  token,
  supplierOptions,
  projectOptions,
  userOptions,
  isLoadingProjects = false,
  isLoadingUsers = false,
  selectedProject = null,
  selectedUser = null,
  onProjectChange,
  onUserChange,
  onCancel,
  onConfirm,
  isSubmitting = false,
  submitError = '',
  disabled = false,
}) => {
  const isPrepaymentQuote = reviewData?.mode === 'prepayment';

  useEffect(() => {
    if (!reviewData) return;
    logInfo(LOG_CONTEXT, 'OCR review step opened', {
      mode: reviewData.mode || 'products',
      requiresManualReview: reviewData.requires_manual_review === true,
      itemCount: Array.isArray(reviewData.items) ? reviewData.items.length : 0,
    });
  }, [reviewData]);

  const [prepaymentAmount, setPrepaymentAmount] = useState(
    reviewData?.header_fields?.prepayment_amount != null
      ? String(reviewData.header_fields.prepayment_amount)
      : ''
  );
  const [prepaymentCurrency, setPrepaymentCurrency] = useState(
    reviewData?.header_fields?.prepayment_currency || ''
  );
  const [supplierId, setSupplierId] = useState(
    reviewData?.supplier_candidate?.id != null
      ? String(reviewData.supplier_candidate.id)
      : ''
  );
  const [demandRef, setDemandRef] = useState(
    String(reviewData?.header_fields?.corporate_demand_ref || '')
  );
  const [budgetLineId, setBudgetLineId] = useState(null);
  const [budget, setBudget] = useState(
    reviewData?.header_fields?.budget == null
      ? ''
      : String(reviewData.header_fields.budget)
  );
  const [ocrSupplierCredits, setOcrSupplierCredits] = useState([]);
  const [isLoadingOcrSupplierCredits, setIsLoadingOcrSupplierCredits] = useState(false);
  const [fundingCreditId, setFundingCreditId] = useState('');
  const [validationError, setValidationError] = useState('');
  const [rows, setRows] = useState(() =>
    (Array.isArray(reviewData?.items) ? reviewData.items : []).map(row => ({
      line_index: row.line_index,
      cat_num_raw: row.cat_num_raw || '',
      cat_num_normalized: row.cat_num_normalized || row.cat_num_raw || '',
      description: row.description || '',
      product_candidate_id:
        row.product_candidate_id != null ? String(row.product_candidate_id) : '',
      product_exists: row.product_exists === true,
      quantity: normalizeQuantityInput(row.quantity),
      price: row.price == null ? '' : String(row.price),
      discount: row.discount == null ? '' : String(row.discount),
      discount_type: String(row.discount ?? '').trim() ? 'percent' : '',
      price_after_discount: String(row.discount ?? '').trim()
        ? computePriceAfterDiscount(row.price, row.discount)
        : row.price_after_discount == null || String(row.price_after_discount) === ''
          ? computePriceAfterDiscount(row.price, row.discount)
          : String(row.price_after_discount),
      currency: row.currency || '',
      unit: String(row.unit || '').trim() || 'Package',
      line_confidence: row.line_confidence,
      issues: Array.isArray(row.issues) ? row.issues : [],
      needs_review: row.needs_review === true,
      removed: false,
      mapping_type:
        row.product_exists === true && row.product_candidate_id != null
          ? 'existing'
          : 'new',
    }))
  );

  const activeRows = useMemo(() => rows.filter(r => !r.removed), [rows]);
  const rowUsesExistingProduct = row =>
    row.mapping_type === 'existing' &&
    row.product_candidate_id &&
    !isNaN(Number(row.product_candidate_id));

  useEffect(() => {
    if (!supplierId || !token) {
      setOcrSupplierCredits([]);
      setIsLoadingOcrSupplierCredits(false);
      return;
    }
    let isCancelled = false;
    setIsLoadingOcrSupplierCredits(true);
    logInfo(LOG_CONTEXT, 'Supplier credits fetch start', {
      supplierId: String(supplierId),
    });
    getSupplierCredits(token, supplierId)
      .then(credits => {
        if (isCancelled) return;
        const list = Array.isArray(credits) ? credits : [];
        setOcrSupplierCredits(list);
        logInfo(LOG_CONTEXT, 'Supplier credits fetch success', {
          supplierId: String(supplierId),
          count: list.length,
        });
      })
      .catch(error => {
        if (isCancelled) return;
        setOcrSupplierCredits([]);
        logWarn(LOG_CONTEXT, 'Supplier credits fetch failed', {
          supplierId: String(supplierId),
          message: error?.message || 'unknown error',
        });
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingOcrSupplierCredits(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [supplierId, token]);

  const setRow = (idx, patch) => {
    setRows(prev =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, ...patch };
        if (
          Object.prototype.hasOwnProperty.call(patch, 'price') ||
          Object.prototype.hasOwnProperty.call(patch, 'discount')
        ) {
          if (Object.prototype.hasOwnProperty.call(patch, 'discount')) {
            next.discount_type = String(next.discount || '').trim() ? 'percent' : '';
          }
          next.price_after_discount = computePriceAfterDiscount(
            next.price,
            next.discount
          );
          if (Object.prototype.hasOwnProperty.call(patch, 'discount')) {
            logInfo(LOG_CONTEXT, 'quote line discount applied as percent', {
              lineIndex: next.line_index ?? idx,
              hasDiscount: String(next.discount || '').trim() !== '',
            });
          }
        }
        return next;
      })
    );
  };

  const validateBeforeSubmit = () => {
    if (!supplierId) return 'Supplier is required.';
    const demandRefText = String(demandRef || '').trim();
    if (isPrepaymentQuote) {
      if (!demandRefText) return 'Corporate demand ref is required.';
      if (
        !isValidNumericInput(prepaymentAmount, { required: true })
      ) {
        return 'Prepayment amount must be a valid number.';
      }
      if (!prepaymentCurrency) return 'Prepayment currency is required.';
      const prepaymentFunding = buildQuoteFundingFields({
        budgetLineId,
        budget,
        fundingCreditId,
        items: [],
        isPrepayment: true,
        prepaymentAmount,
      });
      if (!prepaymentFunding.ok) {
        return prepaymentFunding.error;
      }
      return '';
    }
    const hasBudgetLine =
      budgetLineId != null && String(budgetLineId).trim() !== '';
    const budgetText = String(budget || '').trim();
    const hasBudget = budgetText !== '';
    const hasFundingCredit = String(fundingCreditId || '').trim() !== '';
    if (!hasBudgetLine && !hasBudget && !hasFundingCredit) {
      return 'Either a budget or supplier credit is required.';
    }
    if (activeRows.length === 0) return 'At least one non-removed item is required.';

    for (const r of activeRows) {
      const qtyParsed = parseNumericInput(r.quantity, { integerOnly: true });
      if (qtyParsed === null || qtyParsed <= 0) {
        return `Line ${r.line_index + 1}: quantity must be a positive integer.`;
      }
      if (!isValidNumericInput(r.price, { required: true })) {
        return `Line ${r.line_index + 1}: price must be numeric.`;
      }
      if (!r.currency) {
        return `Line ${r.line_index + 1}: currency is required.`;
      }
      if (!rowUsesExistingProduct(r)) {
        if (!String(r.cat_num_normalized || '').trim()) {
          return `Line ${r.line_index + 1}: catalog number is required for new product.`;
        }
        if (!String(r.description || '').trim()) {
          return `Line ${r.line_index + 1}: product name is required for new product.`;
        }
        if (!String(r.unit || '').trim()) {
          return `Line ${r.line_index + 1}: unit is required for new product.`;
        }
      }
    }
    return '';
  };

  const handleConfirm = () => {
    const err = validateBeforeSubmit();
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError('');
    if (isPrepaymentQuote) {
      const prepaymentFunding = buildQuoteFundingFields({
        budgetLineId,
        budget,
        fundingCreditId,
        items: [],
        isPrepayment: true,
        prepaymentAmount,
      });
      const payload = {
        supplier: Number(supplierId),
        corporate_demand_ref: String(demandRef).trim(),
        ...prepaymentFunding.fields,
        prepayment_amount: String(
          parseNumericInput(prepaymentAmount) ?? prepaymentAmount
        ).trim(),
        prepayment_currency: String(prepaymentCurrency),
        project: selectedProject || null,
        user: selectedUser || null,
        items: [],
      };
      logInfo(LOG_CONTEXT, 'OCR review confirm', {
        mode: 'prepayment',
        supplier: payload.supplier,
        hasFundingCredit: payload.funding_credit_id != null,
        fundingAmount: payload.funding_amount ?? null,
      });
      onConfirm(payload);
      return;
    }
    const buildPriceAfterDiscountForPayload = row => {
      if (String(row.discount || '').trim()) {
        return computePriceAfterDiscount(row.price, row.discount);
      }
      const val =
        row.price_after_discount != null &&
        String(row.price_after_discount).trim() !== ''
          ? String(row.price_after_discount).trim()
          : computePriceAfterDiscount(row.price, row.discount);
      return String(val || '');
    };
    const discountTypeForPayload = row =>
      String(row.discount || '').trim() ? 'percent' : '';
    const mappedItems = activeRows.map(r =>
        rowUsesExistingProduct(r)
          ? {
              product: Number(r.product_candidate_id),
              quantity: parseNumericInput(r.quantity, { integerOnly: true }),
              price: String(parseNumericInput(r.price) ?? r.price),
              discount: String(r.discount || ''),
              discount_type: discountTypeForPayload(r),
              price_after_discount: buildPriceAfterDiscountForPayload(r),
              currency: String(r.currency),
            }
          : {
              supplier_catalog_number: String(
                r.cat_num_normalized || r.cat_num_raw || ''
              ).trim(),
              product_name: String(r.description || '').trim(),
              unit: String(r.unit || '').trim(),
              quantity: parseNumericInput(r.quantity, { integerOnly: true }),
              price: String(parseNumericInput(r.price) ?? r.price),
              discount: String(r.discount || ''),
              discount_type: discountTypeForPayload(r),
              price_after_discount: buildPriceAfterDiscountForPayload(r),
              currency: String(r.currency),
            }
      );
    const productsFunding = buildQuoteFundingFields({
      budgetLineId,
      budget,
      fundingCreditId,
      items: mappedItems,
    });
    if (!productsFunding.ok) {
      setValidationError(productsFunding.error);
      return;
    }
    const payload = {
      supplier: Number(supplierId),
      corporate_demand_ref: String(demandRef).trim(),
      ...productsFunding.fields,
      project: selectedProject || null,
      user: selectedUser || null,
      items: mappedItems,
    };
    logInfo(LOG_CONTEXT, 'OCR review confirm', {
      mode: 'products',
      supplier: payload.supplier,
      itemCount: payload.items.length,
      hasFundingCredit: payload.funding_credit_id != null,
      fundingAmount: payload.funding_amount ?? null,
    });
    onConfirm(payload);
  };

  return (
    <div className="quote-ocr-review-step mt-3">
      <h6 className="d-flex align-items-center gap-2 mb-3">
        <CheckCircle size={16} className="text-primary" />
        OCR parse review (
        {isPrepaymentQuote ? 'prepayment quote' : 'supplier quote'})
      </h6>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Supplier <span className="text-danger ms-1">*</span>
            </Form.Label>
            <DropdownSelect
              optionsList={supplierOptions || []}
              label=""
              placeholder="Select supplier"
              selectedValue={
                (supplierOptions || []).find(
                  s => String(s.value) === String(supplierId)
                ) || null
              }
              setSelectedValue={option => setSupplierId(String(option?.value || ''))}
              disabledOptions={[]}
              disabled={disabled || isSubmitting}
            />
          </Form.Group>
          {reviewData?.supplier_candidate && (
            <div className="small text-muted mt-2">
              Default candidate: {reviewData.supplier_candidate.name || '—'} (
              conf. {reviewData.supplier_candidate.confidence ?? '—'})
            </div>
          )}
        </Col>
        <Col md={6} />
      </Row>

      <Row className="g-3 mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>
              Corporate demand ref
              {isPrepaymentQuote && (
                <span className="text-danger ms-1">*</span>
              )}
            </Form.Label>
            <Form.Control
              value={demandRef}
              onChange={e => setDemandRef(e.target.value)}
              disabled={disabled || isSubmitting}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Project (optional)</Form.Label>
            {isLoadingProjects ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <DropdownSelect
                optionsList={projectOptions || []}
                label=""
                placeholder="Select project"
                selectedValue={
                  (projectOptions || []).find(p => p.value === selectedProject) || null
                }
                setSelectedValue={option =>
                  onProjectChange(option?.value || null)
                }
                disabledOptions={[]}
                disabled={disabled || isSubmitting}
              />
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>User (optional)</Form.Label>
            {isLoadingUsers ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <DropdownSelect
                  optionsList={userOptions || []}
                  label=""
                  placeholder="Select user"
                  selectedValue={findUserOption(userOptions || [], selectedUser)}
                  setSelectedValue={option => {
                    const v = option?.value;
                    onUserChange(option == null || v === '' ? null : v);
                  }}
                  disabledOptions={[]}
                  disabled={disabled || isSubmitting}
                />
                <Form.Text className="text-muted small d-block mt-1">
                  Choose <strong>No user</strong> for no assignment.
                </Form.Text>
              </>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <div className="border rounded p-2 bg-light">
            <div className="small text-muted mb-2">
              Funding source <span className="text-danger ms-1">*</span>{' '}
              <span>(Must choose or enter one)</span>
            </div>
            <Row className="g-2">
              <Col md={6}>
                <BudgetLineSelect
                  token={token}
                  label="Budget"
                  required
                  value={budgetLineId}
                  onChange={id => {
                    setBudgetLineId(id);
                    if (id) {
                      setBudget('');
                      setFundingCreditId('');
                    }
                  }}
                  disabled={disabled || isSubmitting}
                />
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Supplier credit</Form.Label>
                  {isLoadingOcrSupplierCredits ? (
                    <div className="d-flex align-items-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span className="small text-muted">Loading credits...</span>
                    </div>
                  ) : (
                    <Form.Select
                      value={fundingCreditId}
                      onChange={e => setFundingCreditId(e.target.value || '')}
                      disabled={disabled || isSubmitting}
                    >
                      <option value="">None / use budget amount</option>
                      {(ocrSupplierCredits || []).map(credit => (
                        <option key={credit.id} value={credit.id}>
                          {credit.name || `Credit #${credit.id}`} - remaining{' '}
                          {credit.remaining_amount} {credit.currency}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {isPrepaymentQuote && (
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                Prepayment amount <span className="text-danger ms-1">*</span>
              </Form.Label>
              <NumberInput
                name="ocrPrepaymentAmount"
                value={prepaymentAmount}
                onChange={e => setPrepaymentAmount(e.target.value)}
                disabled={disabled || isSubmitting}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                Prepayment currency <span className="text-danger ms-1">*</span>
              </Form.Label>
              <Form.Select
                value={prepaymentCurrency}
                onChange={e => setPrepaymentCurrency(e.target.value)}
                disabled={disabled || isSubmitting}
              >
                <option value="">-- Select currency --</option>
                <option value="NIS">NIS</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      )}

      {!isPrepaymentQuote && (
      <>
      <div
        className="mb-2 d-flex align-items-center gap-2"
        style={{ width: '1500px', maxWidth: '100%', margin: '0' }}
      >
        <strong>Items</strong>
        <Badge bg="secondary">{activeRows.length} active</Badge>
      </div>
      <Table
        striped
        bordered
        hover
        responsive
        size="sm"
        className="small bg-white"
        style={{
          tableLayout: 'fixed',
          width: '1500px',
          maxWidth: '100%',
          margin: '0',
        }}
      >
        <colgroup>
          <col style={{ width: '4%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '6%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '8%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>#</th>
            <th>Catalog #</th>
            <th>Description / name</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Discount %</th>
            <th>Price after discount</th>
            <th>Currency</th>
            <th>Unit</th>
            <th>Issues</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={`ocr-row-${row.line_index}-${idx}`} className={row.removed ? 'text-muted' : ''}>
              <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{row.line_index + 1}</td>
              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                <Form.Control
                  size="sm"
                  value={row.cat_num_normalized}
                  onChange={e => setRow(idx, { cat_num_normalized: e.target.value })}
                  disabled={disabled || isSubmitting || row.removed}
                  className="text-break"
                />
              </td>
              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={row.description}
                  onChange={e => setRow(idx, { description: e.target.value })}
                  disabled={disabled || isSubmitting || row.removed}
                  style={{ minWidth: 180 }}
                />
              </td>
              <td>
                <NumberInput
                  name={`ocrQty-${idx}`}
                  size="sm"
                  integerOnly
                  value={row.quantity}
                  onChange={e => setRow(idx, { quantity: e.target.value })}
                  disabled={disabled || isSubmitting || row.removed}
                  className="text-center"
                />
              </td>
              <td>
                <NumberInput
                  name={`ocrPrice-${idx}`}
                  size="sm"
                  value={row.price}
                  onChange={e => setRow(idx, { price: e.target.value })}
                  disabled={disabled || isSubmitting || row.removed}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  value={row.discount}
                  onChange={e => setRow(idx, { discount: e.target.value })}
                  disabled={disabled || isSubmitting || row.removed}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  value={row.price_after_discount || '—'}
                  disabled
                  plaintext={false}
                  readOnly
                />
              </td>
              <td>
                <Form.Select
                  size="sm"
                  value={row.currency}
                  onChange={e => setRow(idx, { currency: e.target.value })}
                  disabled={disabled || isSubmitting || row.removed}
                >
                  <option value="">--</option>
                  <option value="NIS">NIS</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </Form.Select>
              </td>
              <td>
                <Form.Control
                  size="sm"
                  value={row.unit}
                  onChange={e => setRow(idx, { unit: e.target.value })}
                  disabled={
                    disabled ||
                    isSubmitting ||
                    row.removed ||
                    rowUsesExistingProduct(row)
                  }
                />
              </td>
              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {row.needs_review && <Badge bg="warning" text="dark" className="me-1">Needs review</Badge>}
                {(row.issues || []).map(issue => (
                  <Badge bg="light" text="dark" className="me-1" key={`${row.line_index}-${issue}`}>
                    {issue}
                  </Badge>
                ))}
              </td>
              <td>
                <Button
                  size="sm"
                  variant={row.removed ? 'outline-secondary' : 'outline-danger'}
                  onClick={() => setRow(idx, { removed: !row.removed })}
                  disabled={disabled || isSubmitting}
                >
                  <Trash size={12} className="me-1" />
                  {row.removed ? 'Restore' : 'Remove'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      </>
      )}

      {reviewData?.requires_manual_review && (
        <Alert variant="warning" className="small py-2 mb-3">
          {isPrepaymentQuote
            ? 'Manual review recommended — verify supplier, prepayment amount, and currency before creating the quote.'
            : 'Manual review recommended — verify parsed fields before creating the quote.'}
        </Alert>
      )}

      {(reviewData?.global_issues || []).length > 0 && (
        <Alert variant="warning" className="small py-2">
          <div className="d-flex align-items-start gap-2">
            <ExclamationTriangle className="mt-1" />
            <div>
              <div className="fw-semibold">Global issues</div>
              {(reviewData.global_issues || []).map((issue, i) => (
                <div key={`global-issue-${i}`}>{String(issue)}</div>
              ))}
            </div>
          </div>
        </Alert>
      )}

      {(reviewData?.created_products_preview || []).length > 0 && (
        <Alert variant="info" className="small py-2">
          Created products preview:{' '}
          {(reviewData.created_products_preview || [])
            .map(p => `${p.cat_num}: ${p.name}`)
            .join(' | ')}
        </Alert>
      )}

      {validationError && (
        <Alert variant="danger" className="py-2 small">
          {validationError}
        </Alert>
      )}
      {submitError && (
        <Alert variant="danger" className="py-2 small">
          {submitError}
        </Alert>
      )}

      <div className="d-flex gap-2 mt-3">
        <Button
          variant="success"
          onClick={handleConfirm}
          disabled={disabled || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            isPrepaymentQuote
              ? 'Create prepayment quote from review'
              : 'Create quote from review'
          )}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onCancel}
          disabled={disabled || isSubmitting}
        >
          Back
        </Button>
      </div>
    </div>
  );
};

export default QuoteOcrReviewStep;
